import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, KeyRound, Database, Users, Check, Trash2, Loader2 } from "lucide-react";
import { api, errMsg, adminLab, setAdminLab } from "@/lib/api";

const BLANK = { name: "", slug: "", blurb: "", accent: "#E07A5F", access_code: "" };

export default function Labs() {
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = () => api.adminLabs().then(setRows).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const lab = await api.createLab(draft);
      toast.success(`${lab.name} created — it starts with an empty archive`);
      setDraft(BLANK);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const patch = async (key, body, msg) => {
    try { await api.patchLab(key, body); toast(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const switchTo = (slug) => { setAdminLab(slug); window.location.reload(); };

  const remove = async (l) => {
    if (!window.confirm(`Delete ${l.name} for good?\n\nIts ${l.docs} documents, ${l.chunks} passages and ${l.sessions} sessions are erased. This cannot be undone.`)) return;
    setDeleting(l.slug);
    try {
      await api.deleteLab(l.slug);
      toast.success(`${l.name} deleted`);
      if (adminLab() === l.slug) {           // never leave the console pointed at a Lab that's gone
        const left = (rows || []).filter((r) => r.slug !== l.slug);
        setAdminLab(left[0]?.slug || "");
        window.location.reload();
        return;
      }
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setDeleting(null); }
  };

  if (!rows) return <p className="text-sm text-[#6E7582]">Loading…</p>;
  const current = adminLab() || rows[0]?.slug;

  return (
    <div className="space-y-8" data-testid="labs-page">
      <header>
        <p className="eyebrow">Labs</p>
        <h2 className="mt-1 font-display text-3xl font-semibold tracking-tight">One Griot, many archives</h2>
        <p className="mt-1 text-sm text-[#6E7582]">Each Lab keeps its own memory, persona, voice, language and spend caps. Griot never answers across Labs.</p>
        <p className="mt-2 text-xs text-[#6E7582]">Closing a Lab removes it from the picker <em>and</em> refuses new conversations, even by direct link or QR code. Its archive is untouched — reopen any time. To pause every Lab at once, use the kill switch in Guardrails.</p>
      </header>

      <section className="grid gap-3">
        {rows.map((l) => (
          <div key={l.slug} data-testid={`lab-row-${l.slug}`} className="flex flex-wrap items-center gap-5 rounded-2xl border border-white/[0.06] bg-[#1A1D21] p-5">
            <span className="h-12 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: l.accent }} />
            <div className="min-w-[220px] flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-display text-lg font-medium">{l.name}</h3>
                {l.slug === current && <span className="rounded-full bg-[#2EC4B6]/15 px-2 py-0.5 font-mono text-[10px] text-[#2EC4B6]">EDITING</span>}
                {!l.active && <span className="rounded-full border border-[#E9C46A]/40 px-2 py-0.5 font-mono text-[10px] text-[#E9C46A]">CLOSED</span>}
              </div>
              <p className="mt-0.5 text-sm text-[#8A919D]">{l.blurb || "No blurb yet"}</p>
              <div className="mt-2 flex flex-wrap gap-4 font-mono text-[11px] text-[#6E7582]">
                <span>/l/{l.slug}</span>
                <span className="flex items-center gap-1"><Database size={11} /> {l.docs} docs · {l.chunks} chunks</span>
                <span className="flex items-center gap-1"><Users size={11} /> {l.sessions} sessions</span>
                {l.requires_code && <span className="flex items-center gap-1 text-[#E9C46A]"><KeyRound size={11} /> {l.access_code}</span>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button data-testid={`lab-edit-${l.slug}`} onClick={() => switchTo(l.slug)} disabled={l.slug === current} className="rounded-full border border-white/10 px-4 py-2 text-xs text-[#A1A8B3] hover:border-[#E07A5F]/60 hover:text-[#F4A261] disabled:opacity-40">
                {l.slug === current ? <span className="flex items-center gap-1.5"><Check size={12} /> current</span> : "Manage this Lab"}
              </button>
              <button data-testid={`lab-visibility-${l.slug}`} onClick={() => patch(l.slug, { active: !l.active }, l.active ? `${l.name} is closed to participants` : `${l.name} is open`)} className="rounded-full border border-white/10 px-4 py-2 text-xs text-[#A1A8B3] hover:border-white/30">
                {l.active ? "Close Lab" : "Open Lab"}
              </button>
              {!l.active && (
                <button data-testid={`lab-delete-${l.slug}`} onClick={() => remove(l)} disabled={deleting === l.slug} className="inline-flex items-center gap-1.5 rounded-full border border-[#E63946]/40 px-4 py-2 text-xs text-[#E63946] hover:bg-[#E63946]/10 disabled:opacity-50">
                  {deleting === l.slug ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border border-white/[0.06] bg-[#1A1D21] p-6" data-testid="lab-create">
        <h3 className="font-display text-base font-medium">Add a Lab</h3>
        <p className="text-xs text-[#6E7582]">It opens with an empty archive — upload that Lab's documents in Memory before inviting anyone.</p>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs text-[#6E7582]">Name</span>
            <input data-testid="lab-name-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value, slug: draft.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 31) })} placeholder="British Council Lagos Lab" className="mt-1 w-full rounded-xl border border-white/10 bg-[#121417] px-3 py-2.5 text-sm text-[#F3EFE6] placeholder:text-[#6E7582] focus:border-[#E07A5F] focus:outline-none" />
          </label>
          <label className="block">
            <span className="text-xs text-[#6E7582]">Link (lowercase, hyphens)</span>
            <div className="mt-1 flex items-center rounded-xl border border-white/10 bg-[#121417] px-3">
              <span className="font-mono text-sm text-[#6E7582]">/l/</span>
              <input data-testid="lab-slug-input" value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} placeholder="lagos" className="w-full bg-transparent py-2.5 font-mono text-sm text-[#F3EFE6] focus:outline-none" />
            </div>
          </label>
          <label className="block sm:col-span-2">
            <span className="text-xs text-[#6E7582]">Blurb on the entry screen</span>
            <input data-testid="lab-blurb-input" value={draft.blurb} onChange={(e) => setDraft({ ...draft, blurb: e.target.value })} placeholder="Ask me what this Lab made and remembered together." className="mt-1 w-full rounded-xl border border-white/10 bg-[#121417] px-3 py-2.5 text-sm text-[#F3EFE6] placeholder:text-[#6E7582] focus:border-[#E07A5F] focus:outline-none" />
          </label>
          <label className="block">
            <span className="text-xs text-[#6E7582]">Accent colour</span>
            <div className="mt-1 flex items-center gap-2 rounded-xl border border-white/10 bg-[#121417] px-3 py-2">
              <input data-testid="lab-accent-input" type="color" value={draft.accent} onChange={(e) => setDraft({ ...draft, accent: e.target.value })} className="h-7 w-10 cursor-pointer bg-transparent" />
              <span className="font-mono text-sm text-[#A1A8B3]">{draft.accent}</span>
            </div>
          </label>
          <label className="block">
            <span className="text-xs text-[#6E7582]">Entry code (optional)</span>
            <input data-testid="lab-code-field" value={draft.access_code} onChange={(e) => setDraft({ ...draft, access_code: e.target.value })} placeholder="leave empty for open access" className="mt-1 w-full rounded-xl border border-white/10 bg-[#121417] px-3 py-2.5 font-mono text-sm text-[#F3EFE6] placeholder:font-sans placeholder:text-[#6E7582] focus:border-[#E07A5F] focus:outline-none" />
          </label>
        </div>
        <button data-testid="lab-create-btn" onClick={create} disabled={busy || !draft.name.trim() || !draft.slug.trim()} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#E07A5F] px-5 py-2.5 text-sm font-semibold text-[#121417] hover:bg-[#F4A261] disabled:opacity-50">
          <Plus size={15} /> {busy ? "Creating…" : "Create Lab"}
        </button>
      </section>
    </div>
  );
}
